// Layer 3 — one comparator vocabulary along the Metallurgist journey (ui-lift.C38).
// Pure derivations: points vs design, Budget never ramp, one Pb figure from rd-kpi-pb-rec.

import type { Case } from '@/versions/v1/schema/case';
import type { KpiTile, KpiTone } from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { TARGETS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { readingComparisonBasis } from '@/versions/v1/shared/comparison-basis';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import {
  deviationStatusPct,
  deviationToTone,
} from '@/versions/v1/modules/overview/Performance.transform';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';

function formatNumber(n: number, decimals = 1): string {
  return n.toFixed(decimals);
}

const PB_REC_READING_ID = 'rd-kpi-pb-rec';
const ZN_REC_READING_ID = 'rd-kpi-zn-rec';
const ZN_REC_SERIES_ID = 'rs-zn-rec';

export function recoveryPtDelta(actual: number, design: number): number {
  return actual - design;
}

export function formatPtVsDesign(actual: number, design: number): string {
  const delta = recoveryPtDelta(actual, design);
  const sign = delta >= 0 ? '+' : '−';
  return `${sign}${Math.abs(delta).toFixed(1)} pt vs design`;
}

export function formatPtDelta(delta: number): string {
  const sign = delta >= 0 ? '+' : '−';
  return `${sign}${Math.abs(delta).toFixed(1)} pt`;
}

function readingAt(id: string, state: SessionState) {
  const projected = readingsAt(state).find((r) => r.id === id);
  const seed = readingByIdLookup(id);
  if (!seed) throw new Error(`Missing reading ${id}`);
  return projected ?? seed;
}

function recoveryActual(id: string, state: SessionState): number {
  const row = readingAt(id, state);
  return row?.value ?? 0;
}

function recoveryDesign(id: string, state: SessionState): number {
  const row = readingAt(id, state);
  return row?.design ?? 0;
}

function budgetForKpi(kpiId: string): number | undefined {
  return TARGETS.find((t) => t.kpiId === kpiId && t.basis === 'budget')?.value;
}

export function consecutivePointsBelowDesign(
  seriesId: string,
  design: number,
  state: SessionState,
): number {
  const view = seriesAt(seriesId, state);
  const points = view.points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  let count = 0;
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i]!.value < design) count += 1;
    else break;
  }
  return count;
}

export function znDaysBelowDesign(state: SessionState): number {
  const design = recoveryDesign(ZN_REC_READING_ID, state);
  return consecutivePointsBelowDesign(ZN_REC_SERIES_ID, design, state);
}

export function znRecoveryBelowDesignTitle(state: SessionState): string {
  const actual = recoveryActual(ZN_REC_READING_ID, state);
  const design = recoveryDesign(ZN_REC_READING_ID, state);
  const pts = Math.abs(recoveryPtDelta(actual, design));
  return `Zinc recovery ${pts.toFixed(1)} pt below design`;
}

export function znVariationDescription(state: SessionState): string {
  const days = znDaysBelowDesign(state);
  const dayLabel = days === 1 ? 'day' : 'days';
  return `Zn recovery below design ${days} ${dayLabel} running`;
}

export function znVariationMagnitude(state: SessionState): string {
  const actual = recoveryActual(ZN_REC_READING_ID, state);
  const design = recoveryDesign(ZN_REC_READING_ID, state);
  const budget = budgetForKpi('kpi-zn-rec');
  const pt = formatPtDelta(recoveryPtDelta(actual, design));
  const budgetPart =
    budget !== undefined ? ` · Budget ${formatNumber(budget, 1)} %` : '';
  return `${formatNumber(actual, 1)} % vs design ${formatNumber(design, 1)} % (${pt})${budgetPart}`;
}

export function znRecoveryExecutiveBullet(state: SessionState): string {
  const actual = recoveryActual(ZN_REC_READING_ID, state);
  const design = recoveryDesign(ZN_REC_READING_ID, state);
  const budget = budgetForKpi('kpi-zn-rec');
  const days = znDaysBelowDesign(state);
  const dayLabel = days === 1 ? 'day' : 'days';
  const budgetPart = budget !== undefined ? ` · Budget ${formatNumber(budget, 1)} %` : '';
  return `Zn recovery ${formatNumber(actual, 1)} % (24-h average)${budgetPart} · design ${formatNumber(design, 1)} % · ${days} ${dayLabel} without improvement`;
}

export function displayCaseTitle(caseItem: Case, state: SessionState): string {
  if (caseItem.id === 'case-zn-recovery') return znRecoveryBelowDesignTitle(state);
  return caseItem.title;
}

export type PbRecoveryTile = {
  value: string;
  sub: string;
  tone: KpiTone;
};

export function pbRecoveryTile(state: SessionState): PbRecoveryTile {
  const actual = recoveryActual(PB_REC_READING_ID, state);
  const design = recoveryDesign(PB_REC_READING_ID, state);
  const budget = budgetForKpi('kpi-pb-rec');
  const pt = formatPtVsDesign(actual, design);
  const budgetPart = budget !== undefined ? ` · budget ${formatNumber(budget, 1)}` : '';
  const tone: KpiTone =
    deviationToTone(deviationStatusPct(actual, design)) === 'ok' ? 'ok' : 'bad';
  return {
    value: `${formatNumber(actual, 1)}%`,
    sub: `Design ${formatNumber(design, 1)}${budgetPart} · ${pt}`,
    tone,
  };
}

export function projectPbRecoveryTile(tile: KpiTile, state: SessionState): KpiTile {
  if (!tile.label.toLowerCase().includes('pb recovery')) return tile;
  const derived = pbRecoveryTile(state);
  return { ...tile, value: derived.value, sub: derived.sub, tone: derived.tone };
}

export function pbRecoveryTableCells(state: SessionState): {
  value: string;
  deltaVsTarget: string;
  deltaVsDesign: string;
} {
  const actual = recoveryActual(PB_REC_READING_ID, state);
  const design = recoveryDesign(PB_REC_READING_ID, state);
  const budget = budgetForKpi('kpi-pb-rec') ?? design;
  const vsBudgetPct = ((actual - budget) / budget) * 100;
  const vsDesignPct = ((actual - design) / design) * 100;
  const signB = vsBudgetPct >= 0 ? '+' : '';
  const signD = vsDesignPct >= 0 ? '+' : '';
  return {
    value: formatNumber(actual, 1),
    deltaVsTarget: `${signB}${vsBudgetPct.toFixed(1)}%`,
    deltaVsDesign: `${signD}${vsDesignPct.toFixed(1)}%`,
  };
}

export type MetRecoveryKpiRead = {
  label: string;
  value: string;
  caption: string;
  tone: VizTone;
  provenance?: string;
};

function recoveryTrendDirection(
  seriesName: 'Zn' | 'Pb',
  state: SessionState,
): 'Falling' | 'Stable' | 'Rising' {
  const seriesId = seriesName === 'Zn' ? 'rs-zn-rec' : 'rs-pb-rec';
  const view = seriesAt(seriesId, state);
  const points = view.points
    .filter((p): p is { at: SeriesTime; value: number } => p.value !== null)
    .slice(-7);
  if (points.length < 2) return 'Stable';
  const first = points[0]!.value;
  const last = points[points.length - 1]!.value;
  if (last < first - 0.2) return 'Falling';
  if (last > first + 0.2) return 'Rising';
  return 'Stable';
}

function recoveryTrendSubline(seriesName: 'Zn' | 'Pb', state: SessionState): string {
  const direction = recoveryTrendDirection(seriesName, state);
  if (direction === 'Stable') return 'Stable · 7-day stable vs prior week';
  const seriesId = seriesName === 'Zn' ? 'rs-zn-rec' : 'rs-pb-rec';
  const view = seriesAt(seriesId, state);
  const points = view.points
    .filter((p): p is { at: SeriesTime; value: number } => p.value !== null)
    .slice(-7);
  if (points.length < 2) return 'Stable · 7-day stable vs prior week';
  const delta = points[points.length - 1]!.value - points[0]!.value;
  const sign = delta >= 0 ? '+' : '−';
  return `${direction} ${sign}${Math.abs(delta).toFixed(1)} pt over 7 days`;
}

export function metRecoveryKpiRead(
  metal: 'Zn' | 'Pb',
  state: SessionState,
): MetRecoveryKpiRead {
  const readingId = metal === 'Zn' ? ZN_REC_READING_ID : PB_REC_READING_ID;
  const actual = recoveryActual(readingId, state);
  const basis = readingComparisonBasis(readingAt(readingId, state));
  const design = recoveryDesign(readingId, state);
  const ptLine = formatPtVsDesign(actual, design);
  const tone = basis
    ? deviationToTone(deviationStatusPct(actual, basis.design ?? design))
    : deviationToTone(deviationStatusPct(actual, design));
  return {
    label: `${metal} recovery`,
    value: `${formatNumber(actual, 1)} % · ${ptLine}`,
    caption: recoveryTrendSubline(metal, state),
    tone,
    provenance: metal === 'Zn' ? '[syn]' : undefined,
  };
}
